<template>
	<div class="container" :class="{ subpage: page.slug !== 'index' }">
		<Html :lang="locale" />
		<Head v-if="page">
			<Title>{{ page.metaTitle }}</Title>
			<Meta name="description" :content="page.metaDescription" />
			<Meta name="robots" :content="page.metaRobots" />
			<Hreflangs v-if="page.slug !== '404'" :langmenu="page.langmenu" />
		</Head>

		<div class="border-rounded top-l" />

		<a
			class="border-rounded instagram"
			href="https://www.instagram.com/cafemarcella/"
			target="_blank"
			aria-label="Instagram"
		/>

		<a
			class="border-rounded tiktok"
			href="	https://www.tiktok.com/@cafemarcella"
			target="_blank"
			aria-label="TikTok"
		/>

		<slot />

		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>

		<navigation />

		<client-only>
			<cookie-consent v-if="defaults[locale].cookieConsent.published" :item="defaults[locale].cookieConsent" />
		</client-only>

		<footers />

		<pop-up
			v-if="page && defaults[locale].popUp.content"
			:image="defaults[locale].popUp.image"
			:content="defaults[locale].popUp.content"
		/>
		<div class="border-rounded bottom-l" />
		<div class="border-rounded bottom-r" />
	</div>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { locale } = useI18n();
const config = useRuntimeConfig();
const route = useRoute();
const { defaults, fetchDefaults } = useDefaults();

await fetchDefaults();
await fetchPage();

const handleScroll = () => {
	const fixedElements = document.querySelectorAll('.fix-on-scroll');
	if (window.pageYOffset >= 50) {
		fixedElements.forEach((el) => el.classList.add('fixed'));
	} else {
		fixedElements.forEach((el) => el.classList.remove('fixed'));
	}
};

onBeforeMount(() => {
	window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
	window.removeEventListener('scroll', handleScroll);
});

const currentUrl = config.public.siteUrl + route.fullPath;
const { website } = defaults.value[locale.value];
const { metaDescription, metaTitle } = page.value;

if (import.meta.server) {
	useSeoMeta({
		twitterCard: metaDescription || '',
		twitterSite: currentUrl || '',
		twitterTitle: metaTitle || '',
		twitterDescription: metaDescription || '',
		twitterCreator: website.restaurantName || '',
		twitterImage: website.structuredImage || '',
		ogTitle: metaTitle || '',
		ogUrl: currentUrl || '',
		ogImage: website.structuredImage || '',
		ogDescription: metaDescription || '',
		ogSiteName: website.restaurantName || '',
	});

	useHead({
		script: [
			{
				defer: true,
				'data-assistant': '676ae626246811e32c043a7b',
				src: 'https://hiring-assistant.soigne.app/sdk.js',
			},
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify({
					'@context': 'http://schema.org',
					'@type': 'Hotel',
					'@id': `${config.public.siteUrl}#restaurant`,
					image: website.structuredImage,
					url: config.public.siteUrl,
					name: website.restaurantName,
					logo: `${config.public.siteUrl}/images/logo-cafe-marcella-small.png`,
					telephone: website.phone,
					email: website.email,
					address: {
						'@type': 'PostalAddress',
						streetAddress: `${website.street} ${website.number}, ${website.city}`,
						postalCode: website.postalCode,
					},
				}),
			},
		],
	});
}
</script>

<style lang="scss" scoped>
.border-rounded {
	background: url('~/assets/images/icon-border-rounded.png') no-repeat center center;
	background-size: 50px;
	width: 50px;
	height: 50px;
	position: absolute;
	top: 30px;
	left: 30px;

	&.bottom-l {
		top: auto;
		bottom: 30px;
		transform: rotate(270deg);
	}

	&.bottom-r {
		inset: auto 20px 30px auto;
		transform: rotate(180deg);
	}

	&.instagram {
		right: 20px;
		left: auto;
		background: url('~/assets/images/icon-instagram.png') no-repeat center center;
		background-size: 45px;
		width: 45px;
		height: 45px;
	}

	&.tiktok {
		right: 80px;
		left: auto;
		background: url('~/assets/images/icon-tiktok.png') no-repeat center center;
		background-size: 45px;
		width: 45px;
		height: 45px;
	}
}

.instagram,
.tiktok {
	position: absolute;
	top: 20px;
	right: 20px;
	color: $cta-color;
	font-weight: 300;
}

@media (max-width: 800px) {
	.border-rounded {
		background: url('~/assets/images/icon-border-rounded-smaller.png') no-repeat center center;
		background-size: 35px;
		width: 35px;
		height: 35px;

		&.bottom-l {
			transform: rotate(270deg);
		}

		&.bottom-r {
			transform: rotate(180deg);
		}

		&.instagram {
			background: url('~/assets/images/icon-instagram-smaller.png') no-repeat center center;
			background-size: 30px;
			width: 30px;
			height: 30px;
		}

		&.tiktok {
			background: url('~/assets/images/icon-tiktok-smaller.png') no-repeat center center;
			background-size: 30px;
			width: 30px;
			height: 30px;
			right: 60px;
		}
	}
}
</style>
